import React, { useState, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { compose } from 'recompose';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { Editor } from '@tinymce/tinymce-react';
import { stripHtmlString } from '../../shared/helpers/General';
import StyledIcon from '../styles/Icon.styled';
import Tooltip from './Tooltip.styled';
import sStyles from './EditTextArea.scss';

export const enhance = compose(withStyles(sStyles));

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  
  .tox {
    border-radius: 0 !important;
  }
  .tox .tox-edit-area::before {
    border: none !important;
  }
`;

const InputInnerGroup = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const InputLabel = styled.label`
  color: ${({ theme }) => theme.editTextArea.label.fontColor};
  font-family: ${({ theme }) => theme.editTextArea.label.fontFamily};
  font-size: ${({ theme }) => theme.editTextArea.label.fontSize};
  font-weight: 600;
`;

const Instructions = styled.div`
  color: ${({ theme }) => theme.textInput.instructions.fontColor};
  font-family: ${({ theme }) => theme.textInput.instructions.fontFamily};
  font-size: ${({ theme }) => theme.textInput.instructions.fontSize};
  font-weight: ${({ theme }) => theme.textInput.instructions.fontWeight};
  padding-left: 5px;
  overflow-wrap: anywhere;
  ${({ openSans }) => (openSans ? openSansFont : null)};
  line-height: normal;
  p{
    margin-bottom:0px;
  }
  ol, ul{
    // white-space: pre-wrap;
  }
`;

const Error = styled.span`
  color: ${({ theme }) => theme.editTextArea.error.fontColor};
  font-family: ${({ theme }) => theme.editTextArea.error.fontFamily};
  font-size: ${({ theme }) => theme.editTextArea.error.fontSize};

  svg {
    justify-content: center;
    margin-right: 5px;
  }
`;

const IconDiv = styled.div`
  color: ${({ theme }) => theme.editTextArea.disabled.fontColor};
  position: relative;
  right: 28px;
  top: 9px;
  width: 0;
`;

const openSansFont = css`
  font-family: ${({ theme }) => theme.textInput.fontFamilyOpenSans};
`;

const Required = styled.span`
  color: ${({ theme }) => theme.defaults.errorColor};
  padding-left: 5px;
`;

const TooltipContainer = styled.span`
  padding-left: 5px;
`;

const Wrapper = styled.div`
  width: 100%;
  background-color: ${props =>
    props.readOnly ? ({ theme }) => theme.editTextArea.disabled.backgroundColor : '#ffffff'};
  border: ${props =>
    props.hasFocus
      ? ({ theme }) => theme.editTextArea.focus.border
      : props.errorMsg && props.errorMsg.length > 0
      ? ({ theme }) => theme.editTextArea.error.border
      : props.readOnly
      ? ({ theme }) => theme.editTextArea.disabled.border
      : ({ theme }) => theme.editTextArea.border};
  color: ${props =>
    props.readOnly
      ? ({ theme }) => theme.editTextArea.disabled.fontColor
      : ({ theme }) => theme.editTextArea.fontColor};
  cursor: ${props => (props.readOnly ? 'not-allowed' : 'pointer')};
  font-family: ${({ theme }) => theme.editTextArea.fontFamily};
`;

const CharLimit = styled.div`
  color: #555555;
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

function EditTextAreaTinyMCE(props) {
  const [hasFocus, setHasFocus] = useState(false);
  const [openSansFont, setOpenSansFont] = useState(props.openSans);
  const [charCount, setCharCount] = useState(0);
  const [editorContent, setEditorContent] = useState(props.value || '');
  const isFirstMount = useRef(true);
  const editorRef = useRef(null);
  const skipFirstChange = useRef(true);
  const lastValueRef = useRef(props.value);
  
  // By default, allow image pasting. Only block when isDisableAllowImagePaste is true
  const shouldBlockImages = props.isDisableAllowImagePaste === true;

  useEffect(() => {
    if (!isFirstMount.current && props.value !== editorContent) {
      setEditorContent(props.value || '');
    }
    isFirstMount.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  useEffect(() => {
    if (editorRef.current && props.value !== lastValueRef.current) {
      const editor = editorRef.current;
      if (editor.getContent() !== (props.value || '')) {
        editor.setContent(props.value || '');
      }
      lastValueRef.current = props.value;
    }
  }, [props.value]);

  let cleanseInstructions = null;
  if (props.instructions && stripHtmlString(props.instructions).trim().length > 0) {
    const updatedIntro = !!props.instructions ? props.instructions.replace(/<p><\/p>/g, '<br />') : '';
    cleanseInstructions = updatedIntro;
  }

  useEffect(() => {
    if (props.setUpdateToken) {
      sendTextToEditor(props.setUpdateToken);
    }
  }, [props.setUpdateToken]);

  const sendTextToEditor = (text) => {
    if (window.insertTextToTinyMCE) {
      window.insertTextToTinyMCE(text);
    } else {
      //console.log('Text to insert:', text);
    }
  };

  const handleEditorInit = (evt, editor) => {
    editorRef.current = editor;
    skipFirstChange.current = true; // Reset on every mount/init
  };

  const handleEditorChange = (newContent) => {
    if (props.readOnly) return; // Block changes if readonly
    if (editorContent !== newContent) {
      setEditorContent(newContent);
      // Only call onChange if not the first change (i.e., not on mount/init)
      if (skipFirstChange.current) {
        skipFirstChange.current = false;
        return;
      }
      lastValueRef.current = newContent;
      if(newContent.length > props.charLimit){
        return false; 
      }
      props?.onChange?.(newContent);
    }
  };

  const handleEditorBlur = () => {
    setHasFocus(false);
  };

  const handleEditorFocus = () => {
    skipFirstChange.current = false;
    setHasFocus(true);
  };

  // TinyMCE configuration to match Draft.js functionality
  const tinymceConfig = {
    licenseKey: "gpl",
    tinymceScriptSrc: "/tinymce/tinymce.min.js",
    value: editorContent,
    onEditorChange: handleEditorChange,
    onInit: handleEditorInit,
    onBlur: handleEditorBlur,
    onFocus: handleEditorFocus,
    init: {
      branding: false,
      promotion: false,
      height: props.height || 300,
      menubar: false,
      // statusbar: false,
      readonly: props.readOnly || false,
      placeholder: props.placeholder || ' ',
     
      plugins: [
        'advlist',
        'autolink',
        'lists',
        ...(props.isDisabledLink ? [] : ['link']),
        'charmap',
        'preview',
        'anchor',
        'searchreplace',
        'visualblocks',
        'code',
        'fullscreen',
        'insertdatetime',
        'media',
        'table',
        'help',
        'wordcount',
      ],
      toolbar: props.toolbarHidden || props.readOnly ? false : 
        'undo redo | formatselect | ' +
        'bold italic underline strikethrough | ' +
        'alignleft aligncenter alignright alignjustify | ' +
        'bullist numlist outdent indent | ' +
        (props.isDisabledLink ? '' : 'link unlink | ') + 'removeformat | help',
      content_style: `
        body { 
          font-family: ${openSansFont ? 'Open Sans, sans-serif' : 'Helvetica,Arial,sans-serif'}; 
          font-size: 14px;
          margin: 0;
          padding: 10px;
          border: none;
        }
        p { margin: 0 0 10px 0; }
        ol, ul { margin: 0 0 10px 0; }
      `,
      relative_urls: false,
      remove_script_host: false,
      convert_urls: true,
      // Disable image pasting if shouldBlockImages is true
      paste_data_images: !shouldBlockImages,
      // Block image uploads if image pasting is disabled
      ...(shouldBlockImages && {
        images_upload_handler: (blobInfo, progress) => new Promise((resolve, reject) => {
          reject('Image uploads are disabled');
        }),
        automatic_uploads: false,
      }),
      
      urlconverter_callback: function (url, node, on_save, name) {
        if (node && node.nodeName === 'A') {
          if (url && !/^https?:\/\//i.test(url)) {
            return 'https://' + url; // force https://
          }
        }
        return url;
      },
      setup: (editor) => {
        // Set contenteditable attribute for extra safety
        if (props.readOnly) {
          editor.on('init', () => {
            editor.getBody().setAttribute('contenteditable', false);
          });
        }
        // Handle text insertion for setUpdateToken
        if (props.setUpdateToken) {
          window.insertTextToTinyMCE = (text) => {
            editor.insertContent(text);
          };
        }
        // Prevent image pasting if disabled
        if (shouldBlockImages) {
          // Block paste events with image files
          editor.on('paste', (e) => {
            const clipboardData = e.clipboardData || window.clipboardData;
            
            // Check for image files in clipboard
            if (clipboardData && clipboardData.files && clipboardData.files.length > 0) {
              const hasImages = Array.from(clipboardData.files).some(file => 
                file.type.startsWith('image/')
              );
              if (hasImages) {
                e.preventDefault();
                e.stopPropagation();
                if (props.onImagePasteBlocked) {
                  props.onImagePasteBlocked();
                } else {
                  // console.log('Image pasting is blocked');
                }
                return false;
              }
            }
          });
          
          // Remove img tags from pasted HTML content
          editor.on('PastePreProcess', (e) => {
            if (e.content) {
              // Remove all img tags including base64 images
              e.content = e.content.replace(/<img[^>]*>/gi, '');
              // Remove any data URI images
              e.content = e.content.replace(/src=["']data:image[^"']*["']/gi, '');
            }
          });
          
          // Additional safeguard - remove images after paste
          editor.on('PastePostProcess', (e) => {
            const images = e.node.querySelectorAll('img');
            images.forEach(img => img.remove());
          });
          
          // Block drag and drop of images
          editor.on('drop', (e) => {
            const dataTransfer = e.dataTransfer;
            if (dataTransfer && dataTransfer.files && dataTransfer.files.length > 0) {
              const hasImages = Array.from(dataTransfer.files).some(file => 
                file.type.startsWith('image/')
              );
              if (hasImages) {
                e.preventDefault();
                e.stopPropagation();
                if (props.onImagePasteBlocked) {
                  props.onImagePasteBlocked();
                } else {
                  // console.log('Image drop is blocked');
                }
                return false;
              }
            }
          });
          
          // Monitor content changes and remove any images that slip through
          editor.on('BeforeSetContent', (e) => {
            if (e.content) {
              // Remove img tags from any content being set
              e.content = e.content.replace(/<img[^>]*>/gi, '');
            }
          });
          
          // Final safeguard - periodically check and remove images
          editor.on('NodeChange', () => {
            const body = editor.getBody();
            if (body) {
              const images = body.querySelectorAll('img');
              if (images.length > 0) {
                images.forEach(img => img.remove());
                // console.log('Removed images from editor');
              }
            }
          });
        }
      },
    }
  };

  return (
    <InputGroup>
      <InputLabel htmlFor={props.id}>
        {props.label}
        {props.required && <Required>*</Required>}
        {props.tooltip && (
          <TooltipContainer>
            <Tooltip toolTipText={props.tooltip}>
              <StyledIcon type={'InformationCircle'} size={'14px'} />
            </Tooltip>
          </TooltipContainer>
        )}
      </InputLabel>
      {cleanseInstructions && cleanseInstructions.length > 0 && (
        <Instructions
          openSans={openSansFont}
          dangerouslySetInnerHTML={{
            __html: cleanseInstructions,
          }}
        />
      )}
      <InputInnerGroup>
        <Wrapper errorMsg={props.errorMsg} hasFocus={hasFocus} readOnly={props.readOnly}>
          <Editor
            {...tinymceConfig}
            id={props.id}
          />
        </Wrapper>
        {props.icon && props.icon.length > 0 && (
          <IconDiv>
            <StyledIcon type={props.icon} size={'16px'} />
          </IconDiv>
        )}
      </InputInnerGroup>
      {!!props.charLimit && parseInt(props.charLimit) > 0 && (
        <CharLimit>
          {props.charLimit - props?.value?.length || 0} Characters Remaining
        </CharLimit>
      )}
      {props.errorMsg && props.errorMsg.length > 0 && (
        <Error>
          <StyledIcon type={'Warning'} size={'16px'} />
          {props.errorMsg}
        </Error>
      )}
    </InputGroup>
  );
}

export default enhance(EditTextAreaTinyMCE);
